/**
 * Photo upload types for presigned URL generation and upload flow.
 */
import { z } from 'zod';

/**
 * Allowed content types for photo uploads.
 */
export const ALLOWED_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const;

export type AllowedContentType = (typeof ALLOWED_CONTENT_TYPES)[number];

/**
 * Maximum number of photos allowed per order.
 */
export const MAX_PHOTOS = 10;

/**
 * Presigned URL expiry time in seconds (1 hour).
 */
export const UPLOAD_URL_EXPIRY_SECONDS = 3600;

/**
 * Request schema for getting presigned upload URLs.
 */
export const uploadUrlRequestSchema = z.object({
  files: z
    .array(
      z.object({
        filename: z.string().min(1).max(255),
        contentType: z.enum(ALLOWED_CONTENT_TYPES),
      })
    )
    .min(1)
    .max(MAX_PHOTOS),
  /**
   * Starting index for bucket path numbering.
   * Use this to prevent overwrites when uploading photos in batches.
   * E.g., if user has 2 photos and uploads 3 more, pass startIndex=2.
   */
  startIndex: z.number().int().min(0).max(MAX_PHOTOS).optional().default(0),
  /**
   * BIPA biometric consent version accepted by the user.
   * Required before photo upload — validated server-side against BIPA_CONSENT_VERSION.
   * Sent in the request body (rather than read from DB) to avoid race conditions
   * where the consent checkbox was toggled but auto-save hasn't persisted yet.
   */
  biometricConsentVersion: z.string().min(1),
});

export type UploadUrlRequest = z.infer<typeof uploadUrlRequestSchema>;

/**
 * Single upload URL response item.
 */
export interface UploadUrlItem {
  index: number;
  uploadUrl: string;
  bucketPath: string;
  expiresAt: string;
}

/**
 * Response from the upload URLs endpoint.
 */
export interface UploadUrlResponse {
  uploads: UploadUrlItem[];
}

/**
 * Response from `DELETE /api/orders/:id/photos` (issue #831).
 *
 * `deleted` is the number of storage objects actually removed from the order's
 * `user-uploads/{orderId}/` prefix. It can legitimately exceed the length of
 * `preferences.photoUrls` — the deletion is prefix-scoped precisely so it also
 * reaps ORPHANS (objects whose PUT landed after consent was withdrawn, which
 * the client abandoned and never recorded in the survey).
 */
export interface DeleteUploadedPhotosResponse {
  deleted: number;
}

/**
 * Error response from upload endpoints.
 */
export interface UploadApiError {
  error: string;
}

/**
 * Marker value for `PhotoReference.subject` identifying a photo of a
 * real-world companion (sibling, friend, family member, pet) rather than the
 * protagonist.
 *
 * `preferences.photoUrls` is a MIXED-SUBJECT array (issue #822): the child's
 * own reference photos and companion photos live side by side. Consumers MUST
 * filter by `subject` when they mean "the child's photos".
 */
export const COMPANION_PHOTO_SUBJECT = 'companion';

/**
 * Marker value for a photo of the protagonist.
 *
 * NOTE: an **absent** `subject` also means "child" — every photo stored before
 * issue #822 has no `subject` at all. Always branch on
 * `subject === COMPANION_PHOTO_SUBJECT`, never on `subject === CHILD_PHOTO_SUBJECT`,
 * so legacy entries keep behaving exactly as they do today.
 */
export const CHILD_PHOTO_SUBJECT = 'child';

/**
 * Photo reference stored in survey data (after upload).
 * This format is compatible with the AI agent's photoProcessor.ts.
 */
export interface PhotoReference {
  bucketPath: string;
  caption?: string;
  /**
   * Who is depicted. `COMPANION_PHOTO_SUBJECT` for a real-world companion;
   * absent (or anything else) means the child. Deliberately a plain string
   * rather than a union so the server can introduce new values without
   * breaking older clients (CLAUDE.md permissive-wire-types rule).
   */
  subject?: string;
  /** Set iff `subject === COMPANION_PHOTO_SUBJECT`: the companion's stable id. */
  companionId?: string;
}
